import type { BridgeHealth } from "../lib/api";

/**
 * The join's own reliability, stated before any number derived from it.
 *
 * A GSC click and a GA4 session should be roughly one to one. When the median
 * ratio drifts far from 1.0, something is eating the difference: consent
 * rejection, a redirect hop, an ad blocker, a session spanning midnight. That
 * gap is a finding in its own right, and it is also the reason to distrust
 * everything below it.
 */

export type Verdict = "ok" | "caution" | "unusable";

export function verdictOf(h: BridgeHealth): Verdict {
  if (h.overlapDays === 0 || h.matchedPageDays === 0) return "unusable";
  if (h.ratioP50 == null) return "unusable";
  if (h.ratioP50 < 0.4 || h.ratioP50 > 2.5) return "unusable";
  if (h.ratioP50 < 0.7 || h.ratioP50 > 1.4) return "caution";
  const unmatchedShare = h.unmatchedPageDays /
    Math.max(1, h.matchedPageDays + h.unmatchedPageDays);
  if (unmatchedShare > 0.4) return "caution";
  return "ok";
}

export function BridgeHealthBar({ health }: { health: BridgeHealth }) {
  const v = verdictOf(health);
  const unmatchedShare = health.unmatchedPageDays /
    Math.max(1, health.matchedPageDays + health.unmatchedPageDays);

  return (
    <section className={`bh ${v}`}>
      <div className="bh-head">
        <span className="bh-dot" aria-hidden="true" />
        <span className="bh-verdict">
          {v === "ok" && "The join looks sound"}
          {v === "caution" && "Treat these numbers as directional"}
          {v === "unusable" && "Not enough agreement to model outcomes"}
        </span>
        <span className="bh-ratio num">
          {health.ratioP50 == null
            ? "no matched days"
            : `${health.ratioP50.toFixed(2)} sessions per click`}
        </span>
      </div>

      <div className="bh-stats">
        <span><strong className="num">{health.overlapDays}</strong> days where both sources have data</span>
        <span><strong className="num">{health.gscClicks.toLocaleString()}</strong> clicks</span>
        <span><strong className="num">{health.ga4Sessions.toLocaleString()}</strong> organic sessions</span>
        {health.ratioP10 != null && health.ratioP90 != null && (
          <span className="num">
            ratio p10–p90 {health.ratioP10.toFixed(2)}–{health.ratioP90.toFixed(2)}
          </span>
        )}
        {unmatchedShare > 0.05 && (
          <span>{Math.round(unmatchedShare * 100)}% of page-days had clicks but no sessions</span>
        )}
      </div>

      <p className="bh-note">
        {health.overlapDays === 0 ? (
          <>
            The two exports do not yet cover any of the same days. The GA4 export
            does not backfill, so this fills in from the day you enabled it.
          </>
        ) : health.ratioP50 != null && health.ratioP50 < 0.7 ? (
          <>
            GA4 is recording fewer sessions than Search Console records clicks.
            That gap is usually consent rejection, an ad blocker, or a redirect
            losing the referrer. Worth investigating on its own, separately from
            anything below.
          </>
        ) : health.ratioP50 != null && health.ratioP50 > 1.4 ? (
          <>
            GA4 is recording more sessions than Search Console records clicks,
            which usually means non-organic traffic is being attributed to
            google/organic, or sessions are restarting mid-visit.
          </>
        ) : (
          <>
            Everything below the click is modelled, not measured. Clicks are
            allocated to sessions in proportion to each query&apos;s share of that
            page&apos;s clicks on that day.
          </>
        )}
      </p>
    </section>
  );
}
